/**
 * CurrencyRole - Role para valores monetários
 *
 * IMPORTANTE: Currency é diferente das outras roles!
 * - NÃO tem convert (conversão entre moedas é operação de negócio)
 * - O tipo de moeda (BRL, USD) é METADATA, não variante
 * - Apenas 3 pilares: validate, cast, format
 *
 * @example
 * // Functional style (preferido)
 * import { validateCurrency, castCurrency, formatCurrency } from '@attrx/role-morphic/currency';
 *
 * validateCurrency(100.50);                      // { valid: true, errors: [] }
 * castCurrency('R$ 1.500,50');                   // 1500.50
 * formatCurrency(1500.50, { currency: 'BRL' }); // "R$ 1.500,50"
 */

import { validateCurrency, type CurrencyValidationConfig } from "./validate";
import { castCurrency, tryCastCurrency } from "./cast";
import { formatCurrency, type CurrencyFormatOptions } from "./format";
import { CURRENCY_CONFIGS, type CurrencyCode } from "./constants";

// =============================================================================
// CURRENCY ROLE CLASS
// =============================================================================

/**
 * CurrencyRole - classe wrapper para os 3 pilares
 *
 * Nota: Currency não estende SimpleRole porque não tem convert.
 * É uma role especial com apenas validate, cast e format.
 */
export class CurrencyRole {
  readonly name = "currency";

  /**
   * Valida um valor monetário
   */
  validate(
    value: unknown,
    config?: CurrencyValidationConfig
  ): { valid: boolean; errors: string[] } {
    return validateCurrency(value, config);
  }

  /**
   * Verifica se um valor monetário é válido
   */
  isValid(value: unknown, config?: CurrencyValidationConfig): boolean {
    return validateCurrency(value, config).valid;
  }

  /**
   * Faz cast de input para número
   */
  cast(input: unknown): number | null {
    return castCurrency(input);
  }

  /**
   * Versão safe de cast que retorna Result
   */
  tryCast(
    input: unknown
  ): { ok: true; value: number } | { ok: false; error: string } {
    return tryCastCurrency(input);
  }

  /**
   * Formata valor monetário
   */
  format(value: number, options: CurrencyFormatOptions): string {
    return formatCurrency(value, options);
  }

  /**
   * Lista códigos de moeda disponíveis
   */
  getCurrencyCodes(): CurrencyCode[] {
    return Object.keys(CURRENCY_CONFIGS) as CurrencyCode[];
  }

  /**
   * Verifica se um código de moeda é válido
   */
  hasCurrency(code: string): code is CurrencyCode {
    return code.toUpperCase() in CURRENCY_CONFIGS;
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/** Instância singleton da CurrencyRole */
export const currencyRole = new CurrencyRole();
